import * as yup from 'yup';
import { resolveRegionName } from '@helpers/locations';
import { parseISO } from 'date-fns';
import {
    ChatType,
    LocatedInRelationType,
    LocationType,
    MAX_CERTIFICATION_LENGTH,
    MAX_STORE_CODE_LENGTH,
    googleMapsPlaceUrl,
    paymentMethods,
} from '@constants/companies';
import { Industry } from '@constants/account';
import { CATEGORY_DISPLAY_NAME_PROP } from '@constants/language';
import { LOCALE } from '@constants/locale';
import { isPersistedPhoneInput, setInitialPhoneNumberToForm } from '@helpers/phone';
import {
    hasDuplicateSpecialDate,
    hasFutureSpecialHour,
    hasPublishedWorkingHours,
    hasReversedSpecialRange,
    specialHoursToForm,
} from '@helpers/companies';
import { descriptionContainsUrl } from '@helpers/locations';

export const getCompanyFormSchema = (account, initialCompany) => {
    const noUrlOrUnchanged = initialValue => value => value === (initialValue || '') || !descriptionContainsUrl(value);
    const toFormPhone = number => setInitialPhoneNumberToForm(number, account?.locale);
    const phoneUnlessStored = storedPhones =>
        yup.lazy(value =>
            storedPhones.includes(value)
                ? yup.string()
                : yup.string().validatePhone('validation.wrongPhoneFormat').required('validation.required'),
        );

    return yup.object().shape(
        {
            locationName: yup.string().required('validation.required'),
            internalName: yup.string().required('validation.required'),
            specialHours: yup
                .array()
                .test(
                    'specialHoursReversedRange',
                    'validation.specialHoursReversedRange',
                    value => !hasReversedSpecialRange(value),
                )
                .test(
                    'specialHoursDuplicateDate',
                    'validation.specialHoursDuplicateDate',
                    value => !hasDuplicateSpecialDate(value),
                )
                .test(
                    'specialHoursNeedBusinessHours',
                    'validation.specialHoursNeedBusinessHours',
                    function needsBusinessHours(value) {
                        const { workingHoursEnabled, workingHours } = this.parent;
                        return (
                            hasPublishedWorkingHours(workingHoursEnabled, workingHours) || !hasFutureSpecialHour(value)
                        );
                    },
                ),
            status: yup.number().required('validation.required'),
            phoneNumber: phoneUnlessStored(
                initialCompany?.phoneNumber ? [toFormPhone(initialCompany.phoneNumber)] : [],
            ),
            additionalPhones: yup
                .array()
                .of(
                    yup.object().shape({
                        number: phoneUnlessStored((initialCompany?.additionalPhones || []).map(toFormPhone)),
                    }),
                )
                .test('distinct-phones', function (rows) {
                    const digits = value => (isPersistedPhoneInput(value) ? value.replace(/\D/g, '') : null);
                    const seen = new Set([digits(this.parent.phoneNumber)]);
                    const duplicate = (rows || []).findIndex(({ number }) => {
                        const key = digits(number);
                        if (key && seen.has(key)) return true;
                        seen.add(key);
                        return false;
                    });
                    return (
                        duplicate === -1 ||
                        this.createError({
                            path: `${this.path}[${duplicate}].number`,
                            message: 'validation.duplicatePhone',
                        })
                    );
                }),
            phoneExtension: yup.string().when('phoneExtension', {
                is: val => !val?.length,
                then: schema => schema.notRequired(),
                otherwise: schema => schema.matches(/^\d+$/),
            }),
            email: yup.string().nullable().email('validation.wrongEmailFormat'),
            website: yup.string().url('validation.invalidURL'),
            socialLinks: yup.object().shape({
                facebook: yup.string().url('validation.invalidURL'),
                instagram: yup.string().url('validation.invalidURL'),
                x: yup.string().url('validation.invalidURL'),
                linkedIn: yup.string().url('validation.invalidURL'),
                pinterest: yup.string().url('validation.invalidURL'),
                youTube: yup.string().url('validation.invalidURL'),
                tikTok: yup.string().url('validation.invalidURL'),
            }),
            chatLink: yup.string().when('chatLinkAdded', {
                is: true,
                then: schema => schema.validatePhone('validation.wrongPhoneFormat'),
                otherwise: schema => schema.notRequired(),
            }),
            hasBusinessAddress: yup.boolean(),
            locatedIn: yup
                .object()
                .nullable()
                .when(['locatedInEnabled', 'hasBusinessAddress'], {
                    is: (locatedInEnabled, hasBusinessAddress) => locatedInEnabled && hasBusinessAddress,
                    then: schema => schema.required('validation.required'),
                }),
            country: yup.string().required('validation.required'),
            addressLine1: yup.string().when('hasBusinessAddress', {
                is: true,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
            city: yup.string().when('hasBusinessAddress', {
                is: true,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
            postalCode: yup.string().when('hasBusinessAddress', {
                is: true,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
            shortDescription: yup
                .string()
                .max(75, 'validation.maxLength')
                .test(
                    'no-url',
                    'validation.descriptionContainsUrl',
                    noUrlOrUnchanged(initialCompany?.shortDescription),
                ),
            mediumDescription: yup
                .string()
                .max(500, 'validation.maxLength')
                .test(
                    'no-url',
                    'validation.descriptionContainsUrl',
                    noUrlOrUnchanged(initialCompany?.mediumDescription),
                ),
            longDescription: yup
                .string()
                .max(750, 'validation.maxLength')
                .test('no-url', 'validation.descriptionContainsUrl', noUrlOrUnchanged(initialCompany?.longDescription)),
            primaryCategory: yup.array().min(1, 'validation.required'),
            featuredMessage: yup.string().when('featuredMessageEnabled', {
                is: value => !!value,
                then: schema => schema.required('validation.required').max(50, 'validation.maxLength'),
                otherwise: schema => schema.nullable(),
            }),
            featuredMessageUrl: yup.string().when('featuredMessageEnabled', {
                is: value => !!value,
                then: schema =>
                    schema
                        .url('validation.invalidURL')
                        .max(255, 'validation.maxLength')
                        .required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
            firstName: yup.string().when('type', {
                is: value =>
                    account?.industry === Industry.HealthcareAndDental && value === LocationType.HealthcareProfessional,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
            lastName: yup.string().when('type', {
                is: value =>
                    account?.industry === Industry.HealthcareAndDental && value === LocationType.HealthcareProfessional,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
            degreeId: yup.string().when('type', {
                is: value =>
                    account?.locale === LOCALE.UNITED_STATES &&
                    account?.industry === Industry.HealthcareAndDental &&
                    value === LocationType.HealthcareProfessional,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
            storeCode: yup.string().max(MAX_STORE_CODE_LENGTH, 'validation.maxLength'),
            nationalHealthProviderId: yup.string().when('type', {
                is: value =>
                    account?.locale === LOCALE.UNITED_STATES &&
                    account?.industry === Industry.HealthcareAndDental &&
                    value === LocationType.HealthcareProfessional,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
            certifications: yup
                .array()
                .of(yup.object({ name: yup.string().trim().max(MAX_CERTIFICATION_LENGTH, 'validation.maxLength') })),
        },
        [
            ['phoneExtension', 'phoneExtension'],
            ['secondaryCategories', 'secondaryCategories'],
        ],
    );
};

export const mapCompanyToFormValues = (selectedCompany, account, countries, language) => {
    if (!selectedCompany) return {};

    const country = countries?.find(item => item.iso2 === selectedCompany?.country);

    const hasCategories = selectedCompany?.categories?.length > 0;
    const displayProp = CATEGORY_DISPLAY_NAME_PROP[language] || 'displayNameEn';
    const primaryCategory = hasCategories
        ? selectedCompany?.categories
              .filter(item => item.isPrimary)
              .map(item => ({ id: item.id, displayName: item[displayProp] }))
        : [];
    const secondaryCategories = hasCategories
        ? selectedCompany?.categories
              .filter(item => !item.isPrimary)
              .map(item => ({ category: [{ id: item.id, displayName: item[displayProp] }] }))
        : [];

    return {
        // Loaded separately from the menus API after the form resets
        menuIds: [],
        locationName: selectedCompany?.locationName || '',
        internalName: selectedCompany?.internalName || '',
        status: selectedCompany?.status || undefined,
        brand: selectedCompany?.brand?.id || '',
        phoneNumber: setInitialPhoneNumberToForm(selectedCompany?.phoneNumber, account?.locale),
        phoneExtension: selectedCompany?.phoneExtension || '',
        additionalPhones: (selectedCompany?.additionalPhones || []).map(number => ({
            number: setInitialPhoneNumberToForm(number, account?.locale),
        })),
        email: selectedCompany?.email || '',
        website: selectedCompany?.website || '',
        addressLine1: selectedCompany?.addressLine1 || '',
        addressLine2: selectedCompany?.addressLine2 || '',
        city: selectedCompany?.city || '',
        region: resolveRegionName(country, selectedCompany?.region),
        country: selectedCompany?.country || '',
        postalCode: selectedCompany?.postalCode || '',
        hasBusinessAddress: selectedCompany?.hasBusinessAddress || false,
        latitude: selectedCompany?.latitude || '',
        longitude: selectedCompany?.longitude || '',
        locatedInEnabled: !!selectedCompany?.locatedIn?.placeId,
        locatedIn: selectedCompany?.locatedIn?.placeId
            ? {
                  placeId: selectedCompany.locatedIn.placeId,
                  title: selectedCompany.locatedIn.title || '',
                  address: selectedCompany.locatedIn.address || '',
                  mapsUri: googleMapsPlaceUrl(selectedCompany.locatedIn.placeId),
              }
            : null,
        locatedInRelationType: selectedCompany?.locatedIn?.relationType ?? LocatedInRelationType.Independent,
        shortDescription: selectedCompany?.shortDescription || '',
        mediumDescription: selectedCompany?.mediumDescription || '',
        longDescription: selectedCompany?.longDescription || '',
        openingDate: selectedCompany?.openingDate ? new Date(parseISO(selectedCompany?.openingDate)) : null,
        workingHoursEnabled: selectedCompany?.workingHoursEnabled,
        workingHours: selectedCompany?.workingHours || [],
        specialHours: specialHoursToForm(selectedCompany?.specialHours),
        logoUrl: selectedCompany?.logoUrl,
        coverUrl: selectedCompany?.coverUrl,
        chatLink: setInitialPhoneNumberToForm(selectedCompany?.chatLink, account?.locale),
        chatLinkAdded: !!selectedCompany?.chatLink,
        chatType: selectedCompany?.chatType || ChatType.SMS,
        nationalHealthProviderId: selectedCompany?.nationalHealthProviderId || '',
        storeCode: selectedCompany?.storeCode || '',
        paymentMethods: paymentMethods.map(item => ({
            name: item.name,
            value: !!selectedCompany?.paymentMethods?.includes(item.value),
        })),
        services: selectedCompany?.services?.length
            ? selectedCompany.services.map(item => ({ name: item }))
            : [{ name: '' }],
        certifications: selectedCompany?.certifications?.length
            ? selectedCompany.certifications.map(item => ({ name: item }))
            : [{ name: '' }],
        primaryCategory,
        secondaryCategories,
        socialLinks: {
            facebook: selectedCompany?.socialLinks?.facebook || '',
            instagram: selectedCompany?.socialLinks?.instagram || '',
            x: selectedCompany?.socialLinks?.x || '',
            linkedIn: selectedCompany?.socialLinks?.linkedIn || '',
            pinterest: selectedCompany?.socialLinks?.pinterest || '',
            youTube: selectedCompany?.socialLinks?.youTube || '',
            tikTok: selectedCompany?.socialLinks?.tikTok || '',
        },
        type: selectedCompany?.type || LocationType.Location,
        geoModifier: selectedCompany?.geoModifier || '',
        featuredMessage: selectedCompany?.featuredMessage || '',
        featuredMessageUrl: selectedCompany?.featuredMessageUrl || '',
        featuredMessageEnabled: !!selectedCompany?.featuredMessage,
        firstName: selectedCompany?.firstName || '',
        lastName: selectedCompany?.lastName || '',
        degreeId: selectedCompany?.degreeId || '',
        office: selectedCompany?.office || '',
        headshotUrl: selectedCompany?.headshotUrl,
    };
};
